<!-- UpdateSecurityCredentials.vue -->
<template>
  <div>
    <!-- Modal -->
    <div class="modal fade" id="updateCredentialsModal" tabindex="-1" aria-labelledby="updateCredentialsModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header bg-primary text-white">
            <h5 class="modal-title text-white" id="updateCredentialsModalLabel">
              <i class="mdi mdi-shield-refresh me-2"></i>Update MPESA Security Credentials
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <!-- Alert Section -->
            <div class="alert alert-info" role="alert">
              <div class="d-flex">
                <i class="mdi mdi-information-outline font-size-20 me-2"></i>
                <div>
                  <h6 class="alert-heading mb-2">Important Information</h6>
                  <ul class="mb-0">
                    <li><strong>Initiator Name:</strong> Your MPESA portal username</li>
                    <li><strong>Plain Text Password:</strong> Your MPESA portal password</li>
                    <li><strong>Update Frequency:</strong> These credentials must be updated every 30 days</li>
                    <li><strong>Security:</strong> Your password will be encrypted before storage</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Current Status -->
            <div class="row mb-4">
              <div class="col-md-6">
                <div class="card border">
                  <div class="card-body p-3">
                    <h6 class="text-muted mb-2">Current Account</h6>
                    <p class="mb-1"><strong>{{sourceDetails.shortCodeName}}</strong></p>
                    <p class="mb-0"><code>{{sourceDetails.short_code}}</code></p>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card border">
                  <div class="card-body p-3">
                    <h6 class="text-muted mb-2">Credential Status</h6>
                    <span class="badge" :class="credentialStatus.class">
                      {{credentialStatus.text}}
                    </span>
                    <p class="mb-0 mt-1">
                      <small class="text-muted">{{credentialStatus.message}}</small>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Update Form -->
            <form @submit.prevent="updateCredentials">
              <div class="row">
                <div class="col-12 mb-3">
                  <label class="form-label">
                    <i class="mdi mdi-account me-1"></i>Initiator Name (MPESA Portal Username)
                  </label>
                  <input v-model="formData.initiator"
                         class="form-control"
                         type="text"
                         placeholder="Enter your MPESA portal username"
                         :class="{'is-invalid': errors.initiator}"
                         required>
                  <div v-if="errors.initiator" class="invalid-feedback">
                    {{errors.initiator}}
                  </div>
                </div>

                <div class="col-12 mb-3">
                  <label class="form-label">
                    <i class="mdi mdi-lock me-1"></i>Plain Text Password (MPESA Portal Password)
                  </label>
                  <div class="input-group">
                    <input v-model="formData.plaintext"
                           class="form-control"
                           :type="showPassword ? 'text' : 'password'"
                           placeholder="Enter your MPESA portal password"
                           :class="{'is-invalid': errors.plaintext}"
                           required>
                    <button class="btn btn-outline-secondary"
                            type="button"
                            @click="showPassword = !showPassword">
                      <i :class="showPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                    </button>
                    <button class="btn btn-outline-info"
                            type="button"
                            @click="previewEncryption"
                            :disabled="!formData.plaintext.trim() || previewing"
                            title="Preview encrypted password">
                      <i v-if="previewing" class="mdi mdi-loading mdi-spin"></i>
                      <i v-else class="mdi mdi-eye-check"></i>
                    </button>
                    <div v-if="errors.plaintext" class="invalid-feedback">
                      {{errors.plaintext}}
                    </div>
                  </div>
                  <small class="text-muted">
                    This password will be encrypted using Safaricom's public key before storage
                  </small>

                  <!-- Encrypted Preview -->
                  <div v-if="encryptedPreview" class="mt-2">
                    <label class="form-label">
                      <i class="mdi mdi-shield-check me-1"></i>Encrypted Password Preview
                    </label>
                    <textarea v-model="encryptedPreview"
                              class="form-control"
                              rows="3"
                              readonly
                              style="font-family: monospace; font-size: 0.85rem;">
                    </textarea>
                    <small class="text-success">
                      <i class="mdi mdi-check-circle me-1"></i>Encryption successful! This is how your password will be stored.
                    </small>
                  </div>
                </div>

                <div class="col-12 mb-3">
                  <div class="form-check">
                    <input v-model="confirmUpdate"
                           class="form-check-input"
                           type="checkbox"
                           id="confirmUpdate"
                           :class="{'is-invalid': errors.confirmUpdate}">
                    <label class="form-check-label" for="confirmUpdate">
                      I confirm that the above credentials are correct and I understand they will replace the existing ones
                    </label>
                    <div v-if="errors.confirmUpdate" class="invalid-feedback">
                      {{errors.confirmUpdate}}
                    </div>
                  </div>
                </div>

                <!-- Preview Progress -->
                <div v-if="previewing" class="col-12 mb-3">
                  <div class="alert alert-info">
                    <div class="d-flex align-items-center">
                      <div class="spinner-border spinner-border-sm me-2" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <span>Generating encryption preview...</span>
                    </div>
                  </div>
                </div>

                <!-- Encryption Progress -->
                <div v-if="encrypting" class="col-12 mb-3">
                  <div class="alert alert-info">
                    <div class="d-flex align-items-center">
                      <div class="spinner-border spinner-border-sm me-2" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <span>Encrypting password...</span>
                    </div>
                  </div>
                </div>

                <!-- Update Progress -->
                <div v-if="updating" class="col-12 mb-3">
                  <div class="alert alert-primary">
                    <div class="d-flex align-items-center">
                      <div class="spinner-border spinner-border-sm me-2" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <span>Updating credentials...</span>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="updating || encrypting || previewing">
              Cancel
            </button>
            <button type="button"
                    class="btn btn-primary"
                    @click="updateCredentials"
                    :disabled="!canUpdate || updating || encrypting || previewing">
              <i v-if="updating || encrypting" class="mdi mdi-loading mdi-spin me-1"></i>
              <i v-else class="mdi mdi-shield-refresh me-1"></i>
              {{updating ? 'Updating...' : encrypting ? 'Encrypting...' : 'Update Credentials'}}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import {execute2, biller} from "@/api";
import moment from 'moment';

export default {
  name: "UpdateSecurityCredentials",
  props: {
    sourceDetails: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      formData: {
        initiator: '',
        plaintext: ''
      },
      confirmUpdate: false,
      showPassword: false,
      encrypting: false,
      updating: false,
      previewing: false,
      encryptedPreview: '',
      errors: {}
    }
  },
  computed: {
    credentialStatus() {
      if (!this.sourceDetails.date) {
        return {
          text: 'Never Updated',
          class: 'bg-danger',
          message: 'Credentials have never been updated'
        };
      }

      const lastUpdate = moment(this.sourceDetails.date);
      const daysSinceUpdate = moment().diff(lastUpdate, 'days');
      const daysRemaining = 30 - daysSinceUpdate;

      if (daysRemaining <= 0) {
        return {
          text: 'Expired',
          class: 'bg-danger',
          message: `Expired ${Math.abs(daysRemaining)} days ago`
        };
      } else if (daysRemaining <= 7) {
        return {
          text: `${daysRemaining} days left`,
          class: 'bg-warning',
          message: `Expires on ${lastUpdate.clone().add(30, 'days').format('MMM D, YYYY')}`
        };
      } else if (daysRemaining <= 15) {
        return {
          text: `${daysRemaining} days left`,
          class: 'bg-info',
          message: `Expires on ${lastUpdate.clone().add(30, 'days').format('MMM D, YYYY')}`
        };
      } else {
        return {
          text: 'Active',
          class: 'bg-success',
          message: `${daysRemaining} days remaining`
        };
      }
    },

    canUpdate() {
      return this.formData.initiator.trim() !== '' &&
          this.formData.plaintext.trim() !== '' &&
          this.confirmUpdate;
    }
  },
  methods: {
    async encryptPassword(plaintext) {
      try {
        this.encrypting = true;

        const data = new FormData();
        data.append("function", "previewEncryption");
        data.append("plaintext", plaintext);

        const response = await execute2(data, biller);

        // Handle nested response structure
        if (response.data && response.data.success) {
          if (response.data.data && response.data.data.preview) {
            return response.data.data.preview;
          } else if (response.data.preview) {
            return response.data.preview;
          }
        }
        throw new Error('Failed to encrypt password');
      } catch (error) {
        throw new Error('Encryption failed: ' + error.message);
      } finally {
        this.encrypting = false;
      }
    },

    async previewEncryption() {
      if (!this.formData.plaintext.trim()) {
        return;
      }

      try {
        this.previewing = true;
        this.encryptedPreview = '';

        const data = new FormData();
        data.append("function", "previewEncryption");
        data.append("plaintext", this.formData.plaintext);

        const response = await execute2(data, biller);

        // Handle nested response structure
        if (response.data && response.data.success) {
          if (response.data.data && response.data.data.preview) {
            this.encryptedPreview = response.data.data.preview;
          } else if (response.data.preview) {
            this.encryptedPreview = response.data.preview;
          } else {
            throw new Error('Preview data not found in response');
          }
        } else {
          throw new Error('Failed to generate encryption preview');
        }
      } catch (error) {
        if (this.$toast) {
          this.$toast.error('Preview failed: ' + error.message);
        } else {
          alert('Preview failed: ' + error.message);
        }
        console.error('Encryption preview error:', error);
      } finally {
        this.previewing = false;
      }
    },

    async updateCredentials() {
      // Reset errors
      this.errors = {};

      // Validate form
      if (!this.validateForm()) {
        return;
      }

      try {
        // Step 1: Encrypt the password
        const encryptedPassword = await this.encryptPassword(this.formData.plaintext);

        // Step 2: Update the credentials
        this.updating = true;

        const data = new FormData();
        data.append("function", "updateSecurityCredentials");
        data.append("short_code", this.sourceDetails.short_code);
        data.append("security_credential", encryptedPassword);
        data.append("initiator", this.formData.initiator);
        data.append("sessionNames", sessionStorage.getItem("sessionNames"));
        data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

        const response = await execute2(data, biller);

        // Handle nested response structure
        if (response.data && response.data.success) {
          // Check if it's nested under data.data (your API structure)
          if (response.data.data && response.data.data.success) {
            if (this.$toast) {
              this.$toast.success('Security credentials updated successfully!');
            } else {
              alert('Security credentials updated successfully!');
            }
            this.resetForm();
            this.hideModal();
            this.$emit('credentialsUpdated');
          } else if (response.data.success) {
            // Direct success response
            if (this.$toast) {
              this.$toast.success('Security credentials updated successfully!');
            } else {
              alert('Security credentials updated successfully!');
            }
            this.resetForm();
            this.hideModal();
            this.$emit('credentialsUpdated');
          } else {
            throw new Error(response.data.data?.message || response.data.message || 'Failed to update credentials');
          }
        } else {
          throw new Error(response.data?.message || 'Failed to update credentials');
        }

      } catch (error) {
        if (this.$toast) {
          this.$toast.error(error.message);
        } else {
          alert('Error: ' + error.message);
        }
        console.error('Update credentials error:', error);
      } finally {
        this.updating = false;
      }
    },

    validateForm() {
      let isValid = true;

      if (!this.formData.initiator.trim()) {
        this.errors.initiator = 'Initiator name is required';
        isValid = false;
      }

      if (!this.formData.plaintext.trim()) {
        this.errors.plaintext = 'Password is required';
        isValid = false;
      }

      if (!this.confirmUpdate) {
        this.errors.confirmUpdate = 'Please confirm the update';
        isValid = false;
      }

      return isValid;
    },

    resetForm() {
      this.formData = {
        initiator: '',
        plaintext: ''
      };
      this.confirmUpdate = false;
      this.showPassword = false;
      this.encryptedPreview = '';
      this.errors = {};
    },

    loadCurrentCredentials() {
      if (this.sourceDetails.initiator) {
        this.formData.initiator = this.sourceDetails.initiator;
      }
    },

    show() {
      this.resetForm();
      this.loadCurrentCredentials();

      // Show the modal using Bootstrap
      const modal = new bootstrap.Modal(document.getElementById('updateCredentialsModal'));
      modal.show();
    },

    hideModal() {
      const modal = bootstrap.Modal.getInstance(document.getElementById('updateCredentialsModal'));
      if (modal) {
        modal.hide();
      }
    }
  }
}
</script>

<style scoped>
.mdi-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.btn-close-white {
  filter: brightness(0) invert(1);
}

.form-check-input.is-invalid ~ .form-check-label {
  color: #dc3545;
}
</style>